//
// ChatForm - Component for displaying structured form messages in chat
// Handles vehicle descriptions, police sketches, pictures, support info, and other form data
//

import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  useState,
  useMemo,
  memo,
} from 'react';

import { displayJson } from '../../../../utils';
import {
  Column,
  Row,
} from '../../../display';

import ChatMessageControls from './ChatMessageControls';
import ChatPic from './ChatPic';
import ChatPoliceSketch from './ChatPoliceSketch';
import ChatSupportInfo from './ChatSupportInfo';
import ChatVehicle from './ChatVehicle';
import {
  ChatAuthorIcon,
  ChatAuthorName,
  ChatDate,
  ChatItemClick,
  MessageFrame,
  MessageText,
} from './components';

const ChatForm = memo(({
  registerChild,
  translate,
  style,
  message,
  author,
  initials,
  crBlocked,
  color,
  gcatCode,
  getMedia,
  csid,
  createAlert,
  visibleInThisGathering = true,
  isScrolling,
  isVisible,
  grpGpid,
  grpLbl,
  grpNbr,
  groupsMap,
  imagesMap,
  prtnrLogoUrl,
  selectGathering,
}) => {
  const [showControls, setShowControls] = useState(false);
  const {
    qstnId,
    msgTxt,
    id,
    gthrId,
    msgRdatim1,
  } = message;

  const content = useMemo(
    () => {
      if (qstnId === 'vehicleDescr') {
        return (
          <ChatVehicle
            translate={translate}
            message={message}
          />
        );
      }

      if (qstnId === 'policeSketch') {
        return (
          <ChatPoliceSketch
            translate={translate}
            message={message}
            getMedia={getMedia}
            csid={csid}
          />
        );
      }

      if (qstnId === 'picture') {
        return (
          <ChatPic
            translate={translate}
            message={message}
            getMedia={getMedia}
            csid={csid}
          />
        );
      }

      if (qstnId === 'supportInfo') {
        return (
          <ChatSupportInfo
            translate={translate}
            message={message}
          />
        );
      }

      return (
        <MessageText
          className="text-left"
          key="txt"
        >
          <i>
            {`${translate(qstnId, 'questionnairePrompts')}`}
            {(msgTxt) ? ` ${msgTxt}` : null}
          </i>
          <br />
          {displayJson(message)}
        </MessageText>
      );
    },
    [csid, getMedia, message, qstnId, msgTxt, translate],
  );

  const enableAlert = useMemo(
    () => {
      if ((qstnId === 'vehicleDescr')
      || (qstnId === 'policeSketch')
      || (qstnId === 'picture')) {
        return true;
      }

      return false;
    },
    [qstnId],
  );

  return (
    <div
      ref={registerChild}
      key={id}
      className={classNames(
        'w-full py-2 gap-1 flex flex-col relative items-center justify-center overflow-hidden group',
        {
          'px-6': visibleInThisGathering,
          'px-2 opacity-50': !visibleInThisGathering,
        },
      )}
      style={style}
      // https://stackoverflow.com/a/61613591/9316077
      onMouseEnter={() => { setShowControls(true); }}
      onMouseLeave={() => { setShowControls(false); }}
    >
      <ChatItemClick
        gthrId={gthrId}
        selectGathering={selectGathering}
        visibleInThisGathering={visibleInThisGathering}
      />

      <ChatAuthorName
        translate={translate}
        author={author}
        initials={initials}
        crBlocked={crBlocked}
        gcatCode={gcatCode}
        grpNbr={grpNbr}
        prtnrLogoUrl={prtnrLogoUrl}
      />

      <Row
        start={!!author}
        end={!author}
        fullw
      >
        <ChatAuthorIcon
          author={author}
          initials={initials}
          color={color}
          gcatCode={gcatCode}
          grpGpid={grpGpid}
          grpLbl={grpLbl}
          groupsMap={groupsMap}
          imagesMap={imagesMap}
        />

        <MessageFrame
          key={id}
          author={author}
          visibleInThisGathering={visibleInThisGathering}
        >
          {content}
        </MessageFrame>

        <Column
          className={classNames(
            'self-start',
            {
              'order-2': !author,
              'order-4': author,
            },
          )}
          gap={1}
          crossStart={author}
          crossEnd={!author}
        >
          <ChatDate
            dtime={msgRdatim1}
            myMessage={!author}
          />

          <ChatMessageControls
            translate={translate}
            myMessage={!author}
            createAlert={(enableAlert) ? createAlert : undefined}
            showControls={showControls}
            author={author}
            message={message}
            csid={csid}
            isScrolling={isScrolling}
            isVisible={isVisible}
          />
        </Column>

      </Row>
    </div>
  );
});
ChatForm.displayName = 'ChatForm';

ChatForm.propTypes = {
  registerChild: PropTypes.func.isRequired,
  translate: PropTypes.func.isRequired,
  style: PropTypes.shape({}).isRequired,
  author: PropTypes.string,
  initials: PropTypes.string,
  crBlocked: PropTypes.number,
  color: PropTypes.string,
  gcatCode: PropTypes.string,
  getMedia: PropTypes.func.isRequired,
  csid: PropTypes.string.isRequired,
  createAlert: PropTypes.func,
  visibleInThisGathering: PropTypes.bool,
  isScrolling: PropTypes.bool.isRequired,
  isVisible: PropTypes.bool.isRequired,
  grpGpid: PropTypes.string,
  grpLbl: PropTypes.string,
  grpNbr: PropTypes.number,
  groupsMap: PropTypes.instanceOf(Map).isRequired,
  imagesMap: PropTypes.instanceOf(Map).isRequired,
  selectGathering: PropTypes.func,
  message: PropTypes.shape({
    id: PropTypes.number.isRequired,
    gthrId: PropTypes.number,
    // msgId: PropTypes.number.isRequired,
    msgTxt: PropTypes.string,
    msgRdatim1: PropTypes.string.isRequired,
    qstnId: PropTypes.string,
  }).isRequired,
  prtnrLogoUrl: PropTypes.string,
};

export default ChatForm;
